// #920 §5 — Collections in search.
//
// "Collections must appear in gallery/global search results (index the name; a
// hit opens the collection)."
//
// WHAT IS INDEXED, AND WHERE
// The name. The global "Search iYuga" dropdown gains a Collections tab that
// lists the signed-in user's collections whose NAME matches the query, and
// activating a row deep-links to that collection (`/gallery?collection=<id>`),
// which OwnerView opens on arrival.
//
// The collections endpoint (`/api/v2/folders/`) is not a search endpoint: it has
// no documented `search` param and is scoped to one `user`. So the query is sent
// (harmless if ignored) AND the results are matched here, client-side, which
// makes the tab correct either way. The consequence — and it is a real limit,
// not an oversight — is that this covers YOUR OWN collections only. Searching
// other people's public collections needs a server-side index
// (`/api/v2/search-collections/`); see the PR body (BE, Usman).
//
// Pure module: the matcher, the ranking and the deep-link contract are all
// testable without a network or a DOM.

/** Route a collection hit opens. Read back by collectionIdFromSearch below. */
export const COLLECTION_DEEP_LINK_PARAM = "collection";

export const collectionDeepLink = (collectionId, userId = null) => {
  const base = userId == null ? "/gallery" : `/gallery/${userId}`;
  return `${base}?${COLLECTION_DEEP_LINK_PARAM}=${encodeURIComponent(
    String(collectionId)
  )}`;
};

/**
 * The collection id a gallery URL asks to be opened, or null.
 * Accepts a raw query string ("?collection=7") or a full search fragment.
 */
export const collectionIdFromSearch = (search) => {
  if (!search || typeof search !== "string") return null;
  try {
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search
    );
    const raw = params.get(COLLECTION_DEEP_LINK_PARAM);
    return raw ? raw : null;
  } catch (e) {
    return null;
  }
};

/** Normalised form used for matching: case- and diacritic-insensitive. */
export const normalizeForSearch = (value) =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/**
 * Does this collection's NAME match the query?
 * Substring, not fuzzy: a folder list is short and a user typing "trav" expects
 * "Travel", not every name sharing four scattered letters.
 */
export const collectionMatchesQuery = (collection, query) => {
  const q = normalizeForSearch(query);
  if (!q) return false;
  const name = normalizeForSearch(collection?.name ?? collection?.title);
  return name.length > 0 && name.includes(q);
};

// A wheel with no name renders as this label on its card (single source of
// truth: utils/helpers.js's UNTITLED_WHEEL_NAME/getWheelDisplayName). Kept as
// a local literal rather than importing utils/helpers — that module pulls in
// a DOMParser-based htmlToText, and many OwnerView/WheelCard suites already
// mock utils/helpers with a narrow factory that doesn't export it, which
// would silently disable this fallback under those mocks.
const UNTITLED_WHEEL_LABEL = "Untitled Wheel";

/**
 * Does this wheel's title match the query? Mirrors collectionMatchesQuery so
 * ISP-9999 #954's in-gallery search box can filter the wheels grid with the
 * exact same query it already filters collections with — one query, two
 * lists. `wheel_name` is the field the wheels API/grid actually carries
 * (WheelCard.jsx, WheelItem.jsx); `title` is kept as a fallback for any
 * already-groomed shape that normalizes toward it.
 *
 * ISP-9999: a null/empty/whitespace-only name matches against the SAME
 * "Untitled Wheel" label the card itself falls back to displaying — otherwise
 * a wheel showing "Untitled Wheel" on screen is unfindable by that exact text.
 */
export const wheelMatchesQuery = (wheel, query) => {
  const q = normalizeForSearch(query);
  if (!q) return false;
  const rawName = normalizeForSearch(wheel?.wheel_name ?? wheel?.title);
  const name = rawName || normalizeForSearch(UNTITLED_WHEEL_LABEL);
  return name.length > 0 && name.includes(q);
};

/**
 * Rank: a prefix match ("Trav" -> "Travel") beats a mid-word one ("Trav" ->
 * "Business Travel"); ties fall back to the shorter name, then to the server's
 * own order (stable sort).
 */
const matchRank = (collection, query) => {
  const name = normalizeForSearch(collection?.name ?? collection?.title);
  return name.startsWith(normalizeForSearch(query)) ? 0 : 1;
};

/**
 * Filter + rank raw `/api/v2/folders/` rows into search result items.
 * Wheel rows (which the same endpoint can return) are dropped; so are private
 * collections when `includePrivate` is false.
 *
 * @returns {Array<{id, title, count, visibility, deepLink}>}
 */
export const searchCollections = (
  results = [],
  query,
  { includePrivate = true, limit = 10, userId = null } = {}
) => {
  const rows = (Array.isArray(results) ? results : [])
    .filter((f) => f && !f.wheel_segments)
    .filter((f) => includePrivate || !(f.visibility === 2 || f.is_private))
    .filter((f) => collectionMatchesQuery(f, query));

  return rows
    .map((f, index) => ({ f, index }))
    .sort((a, b) => {
      const rank = matchRank(a.f, query) - matchRank(b.f, query);
      if (rank !== 0) return rank;
      const nameA = normalizeForSearch(a.f.name ?? a.f.title);
      const nameB = normalizeForSearch(b.f.name ?? b.f.title);
      if (nameA.length !== nameB.length) return nameA.length - nameB.length;
      return a.index - b.index;
    })
    .slice(0, limit)
    .map(({ f }) => ({
      id: f.id,
      title: f.name ?? f.title ?? "",
      count: f.content_amount ?? f.size ?? null,
      visibility: f.visibility ?? null,
      deepLink: collectionDeepLink(f.id, userId),
    }));
};
